import { useCallback } from 'react';
import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Textarea,
  TextareaProps,
} from '@chakra-ui/react';
import { FieldPath, FieldPathValue, FieldValues, Validate, ValidationRule, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormErrorMessage } from './useFormErrorMessage';
import { isRequiredValueMissing } from './requiredValidation';

export interface FormTextAreaProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormControlProps {
  name: TFieldName;
  label: string;
  description?: React.ReactNode;
  validate?: Validate<FieldPathValue<TFieldValues, TFieldName>, TFieldValues>;
  pattern?: ValidationRule<RegExp>;
  autoComplete?: TextareaProps['autoComplete'];
}

export function FormTextArea<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  isRequired,
  validate,
  pattern,
  autoFocus,
  size,
  autoComplete,
  ...rest
}: FormTextAreaProps<TFieldValues>) {
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  const { t } = useTranslation('forms');

  const errorMessage = useFormErrorMessage(name, label, errors);

  const requiredValidate = useCallback(
    (value: unknown) =>
      !isRequired || !isRequiredValueMissing(value) || t('form.validationError.required', { field: label }),
    [isRequired, label, t]
  );

  return (
    <FormControl isRequired={isRequired} isInvalid={!!errors[name]} {...rest}>
      <FormLabel size={size}>{label}</FormLabel>
      <Textarea
        required={false}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        size={size}
        {...register(name, {
          // A custom validator owns required-ness entirely once supplied (e.g. cross-field
          // "either this or that" rules), so it must not be preempted by the generic check.
          validate: validate ?? requiredValidate,
          pattern,
        })}
      />
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
}
